import { DEFAULT_BALANCING_V1_CONFIG } from "./balancingV1";
import { DEFAULT_CAPACITY_PAYMENTS_CONFIG } from "./capacityPayments";
import { DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG } from "./guaranteesOfOrigin";

export const FLOOR_INPUT_MODES = [
  {
    value: "absolute_per_settlement_period",
    label: "Absolute EUR per settlement period",
  },
  {
    value: "eur_per_mw_year",
    label: "EUR/MW/year",
  },
  {
    value: "eur_per_mw_month",
    label: "EUR/MW/month",
  },
  {
    value: "eur_per_kw_month",
    label: "EUR/kW/month",
  },
];

export const DEFAULT_TOLLING_FLOOR_UPSIDE = {
  floorInputMode: "absolute_per_settlement_period",
  floorAmount: 0,
  settlementFrequency: "monthly",
  applyAvailabilityToFloor: true,
  availabilityAdjustmentPct: 95,
  effectiveFloorPreview: null,
  contractedCapacityMw: null,
  capacityBasis: "InstalledMW",

  floorEscalationMode: "none",
  floorEscalationPct: 0,
  floorEscalationCurve: [],

  projectUpsideSharePct: 20,
  contractTermYears: 10,
  merchantOptimization: true,

  merchantValueBasis: "net_after_charging_cost",
  includedRevenueStreams: {
    dam: true,
    intraday: false,
    ida1: false,
    ida2: false,
    ida3: false,
    balancing: false,
    ancillary: false,
    capacity: false,
    go: false,
    other: false,
  },
  balancingV1: DEFAULT_BALANCING_V1_CONFIG,
  capacityPayments: DEFAULT_CAPACITY_PAYMENTS_CONFIG,
  guaranteesOfOrigin: DEFAULT_GUARANTEES_OF_ORIGIN_CONFIG,

  bankability: {
    counterpartyStatus: "generic_assumption",
    counterpartyType: "not_specified",
    counterpartyName: "",
    creditSupportType: "none",
    creditSupportMonths: null,
  },

  performanceRegime: {
    guaranteedAvailabilityPct: null,
    penaltyCapPctOfAnnualFloor: null,
    plannedOutageAllowanceHours: null,
    guaranteedRtePct: null,
    maxAnnualCycles: null,
    maxAnnualThroughputMWh: null,
    degradationResponsibility: "not_specified",
    augmentationResponsibility: "not_specified",
  },
};

const VALID_FLOOR_INPUT_MODES = new Set(FLOOR_INPUT_MODES.map((mode) => mode.value));

function parseFiniteNumber(value, fallback = 0) {
  if (value === "" || value == null) return fallback;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : fallback;
}

function parseOptionalFiniteNumber(value) {
  if (value === "" || value == null) return null;
  const parsed = Number(String(value).replace(",", "."));
  return Number.isFinite(parsed) ? parsed : null;
}

function clampPercent(value, fallback = 0) {
  const parsed = parseFiniteNumber(value, fallback);
  return Math.min(100, Math.max(0, parsed));
}

function normalizeSettlementFrequency(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (normalized === "quarterly" || normalized === "quarter") return "quarterly";
  if (normalized === "annually" || normalized === "annual" || normalized === "yearly") return "annual";
  return "monthly";
}

export function getSettlementPeriodsPerYear(settlementFrequency) {
  switch (normalizeSettlementFrequency(settlementFrequency)) {
    case "annual":
      return 1;
    case "quarterly":
      return 4;
    case "monthly":
    default:
      return 12;
  }
}

function getSettlementMonths(settlementFrequency) {
  return 12 / getSettlementPeriodsPerYear(settlementFrequency);
}

export function isCapacityFloorInputMode(mode) {
  return mode === "eur_per_mw_year" || mode === "eur_per_mw_month" || mode === "eur_per_kw_month";
}

export function deriveInstalledBessPowerMw(config = {}) {
  const selected = Array.isArray(config?.bess?.selected) ? config.bess.selected : [];
  const totalKw = selected.reduce((sum, battery) => {
    const count = parseFiniteNumber(battery?.count, 1);
    const powerKw = parseFiniteNumber(
      battery?.p_max_d ?? battery?.pMaxD ?? battery?.power ?? battery?.Power,
      0
    );
    return sum + Math.max(0, count) * Math.max(0, powerKw);
  }, 0);

  return totalKw > 0 ? totalKw / 1000 : null;
}

function normalizeFloorInputMode(value) {
  return VALID_FLOOR_INPUT_MODES.has(value)
    ? value
    : DEFAULT_TOLLING_FLOOR_UPSIDE.floorInputMode;
}

export function normalizeTollingFloorUpside(config = {}) {
  const source =
    config?.tollingFloorUpside && typeof config.tollingFloorUpside === "object"
      ? config.tollingFloorUpside
      : {};
  const installedCapacityMw = deriveInstalledBessPowerMw(config);
  const floorEscalationPct =
    source.floorEscalationPct ??
    source.floor_escalation_pct ??
    config.tollingEscalationPct ??
    DEFAULT_TOLLING_FLOOR_UPSIDE.floorEscalationPct;
  const availabilityAdjustmentPct =
    source.availabilityAdjustmentPct ??
    source.availability_adjustment_pct ??
    config.tollingAvailabilityPct ??
    DEFAULT_TOLLING_FLOOR_UPSIDE.availabilityAdjustmentPct;
  const floorInputMode = normalizeFloorInputMode(
    source.floorInputMode ?? source.floor_input_mode
  );
  const sourceBankability =
    source.bankability && typeof source.bankability === "object" ? source.bankability : {};
  const sourcePerformance =
    source.performanceRegime && typeof source.performanceRegime === "object"
      ? source.performanceRegime
      : source.performance_regime && typeof source.performance_regime === "object"
        ? source.performance_regime
        : {};
  const guaranteedAvailabilityPct =
    sourcePerformance.guaranteedAvailabilityPct ??
    sourcePerformance.guaranteed_availability_pct ??
    availabilityAdjustmentPct ??
    null;
  const guaranteedRtePct =
    sourcePerformance.guaranteedRtePct ??
    sourcePerformance.guaranteed_rte_pct ??
    null;
  const maxAnnualCycles =
    sourcePerformance.maxAnnualCycles ??
    sourcePerformance.max_annual_cycles ??
    null;
  const creditSupportType =
    sourceBankability.creditSupportType ??
    sourceBankability.credit_support_type ??
    source.creditSupportType ??
    DEFAULT_TOLLING_FLOOR_UPSIDE.bankability.creditSupportType;

  return {
    ...DEFAULT_TOLLING_FLOOR_UPSIDE,
    ...source,
    floorInputMode,
    floorAmount: parseFiniteNumber(
      source.floorAmount ?? source.floor_amount ?? config.tollingRevenueFloor,
      DEFAULT_TOLLING_FLOOR_UPSIDE.floorAmount
    ),
    settlementFrequency: normalizeSettlementFrequency(
      source.settlementFrequency ??
        source.settlement_frequency ??
        config.tollingSettlementFrequency ??
        DEFAULT_TOLLING_FLOOR_UPSIDE.settlementFrequency
    ),
    applyAvailabilityToFloor:
      source.applyAvailabilityToFloor ??
      source.apply_availability_to_floor ??
      DEFAULT_TOLLING_FLOOR_UPSIDE.applyAvailabilityToFloor,
    availabilityAdjustmentPct: parseFiniteNumber(
      availabilityAdjustmentPct,
      DEFAULT_TOLLING_FLOOR_UPSIDE.availabilityAdjustmentPct
    ),
    effectiveFloorPreview: null,
    contractedCapacityMw:
      parseOptionalFiniteNumber(source.contractedCapacityMw ?? source.contracted_capacity_mw) ??
      installedCapacityMw,
    capacityBasis:
      source.capacityBasis ??
      source.capacity_basis ??
      config.tollingCapacityBasis ??
      DEFAULT_TOLLING_FLOOR_UPSIDE.capacityBasis,
    floorEscalationMode:
      source.floorEscalationMode ??
      source.floor_escalation_mode ??
      (parseFiniteNumber(floorEscalationPct, 0) !== 0 ? "fixed_pct" : "none"),
    floorEscalationPct: parseFiniteNumber(
      floorEscalationPct,
      DEFAULT_TOLLING_FLOOR_UPSIDE.floorEscalationPct
    ),
    floorEscalationCurve: Array.isArray(source.floorEscalationCurve)
      ? source.floorEscalationCurve
      : Array.isArray(source.floor_escalation_curve)
        ? source.floor_escalation_curve
        : DEFAULT_TOLLING_FLOOR_UPSIDE.floorEscalationCurve,
    projectUpsideSharePct: parseFiniteNumber(
      source.projectUpsideSharePct ??
        source.project_upside_share_pct ??
        config.tollingUpsideSharePct,
      DEFAULT_TOLLING_FLOOR_UPSIDE.projectUpsideSharePct
    ),
    contractTermYears: parseFiniteNumber(
      source.contractTermYears ?? source.contract_term_years ?? config.tollingContractYears,
      DEFAULT_TOLLING_FLOOR_UPSIDE.contractTermYears
    ),
    merchantOptimization:
      source.merchantOptimization ??
      source.merchant_optimization ??
      config.useMerchantOptimization ??
      DEFAULT_TOLLING_FLOOR_UPSIDE.merchantOptimization,
    includedRevenueStreams: {
      ...DEFAULT_TOLLING_FLOOR_UPSIDE.includedRevenueStreams,
      ...(source.includedRevenueStreams || source.included_revenue_streams || {}),
    },
    bankability: {
      ...DEFAULT_TOLLING_FLOOR_UPSIDE.bankability,
      ...sourceBankability,
      counterpartyStatus:
        sourceBankability.counterpartyStatus ??
        sourceBankability.counterparty_status ??
        source.counterpartyStatus ??
        DEFAULT_TOLLING_FLOOR_UPSIDE.bankability.counterpartyStatus,
      counterpartyType:
        sourceBankability.counterpartyType ??
        sourceBankability.counterparty_type ??
        source.counterpartyType ??
        DEFAULT_TOLLING_FLOOR_UPSIDE.bankability.counterpartyType,
      counterpartyName:
        sourceBankability.counterpartyName ??
        sourceBankability.counterparty_name ??
        DEFAULT_TOLLING_FLOOR_UPSIDE.bankability.counterpartyName,
      creditSupportType:
        creditSupportType,
      creditSupportMonths:
        creditSupportType === "none"
          ? null
          : parseOptionalFiniteNumber(
              sourceBankability.creditSupportMonths ??
                sourceBankability.credit_support_months ??
                source.creditSupportMonths
            ),
    },
    performanceRegime: {
      ...DEFAULT_TOLLING_FLOOR_UPSIDE.performanceRegime,
      ...sourcePerformance,
      guaranteedAvailabilityPct: parseOptionalFiniteNumber(guaranteedAvailabilityPct),
      penaltyCapPctOfAnnualFloor: parseOptionalFiniteNumber(
        sourcePerformance.penaltyCapPctOfAnnualFloor ??
          sourcePerformance.penalty_cap_pct_of_annual_floor
      ),
      plannedOutageAllowanceHours: parseOptionalFiniteNumber(
        sourcePerformance.plannedOutageAllowanceHours ??
          sourcePerformance.planned_outage_allowance_hours
      ),
      guaranteedRtePct: parseOptionalFiniteNumber(guaranteedRtePct),
      maxAnnualCycles: parseOptionalFiniteNumber(maxAnnualCycles),
      maxAnnualThroughputMWh: parseOptionalFiniteNumber(
        sourcePerformance.maxAnnualThroughputMWh ??
          sourcePerformance.max_annual_throughput_mwh
      ),
      degradationResponsibility:
        sourcePerformance.degradationResponsibility ??
        sourcePerformance.degradation_responsibility ??
        DEFAULT_TOLLING_FLOOR_UPSIDE.performanceRegime.degradationResponsibility,
      augmentationResponsibility:
        sourcePerformance.augmentationResponsibility ??
        sourcePerformance.augmentation_responsibility ??
        DEFAULT_TOLLING_FLOOR_UPSIDE.performanceRegime.augmentationResponsibility,
    },
  };
}

export function calculateTollingFloorPreview(tollingFloorUpside = {}) {
  const normalized = normalizeTollingFloorUpside({ tollingFloorUpside });
  const periodsPerYear = getSettlementPeriodsPerYear(normalized.settlementFrequency);
  const settlementMonths = getSettlementMonths(normalized.settlementFrequency);
  const floorAmount = Math.max(0, parseFiniteNumber(normalized.floorAmount, 0));
  const capacityMw = Math.max(0, parseFiniteNumber(normalized.contractedCapacityMw, 0));
  const availabilityPct = normalized.applyAvailabilityToFloor
    ? clampPercent(normalized.availabilityAdjustmentPct, 100)
    : 100;

  let rawFloor = floorAmount;
  let rawFloorPerSettlement = floorAmount;
  let rawFloorUnit = "settlement period";

  if (normalized.floorInputMode === "eur_per_mw_year") {
    const annualFloor = capacityMw * floorAmount;
    rawFloor = annualFloor;
    rawFloorPerSettlement = annualFloor / periodsPerYear;
    rawFloorUnit = "year";
  } else if (normalized.floorInputMode === "eur_per_mw_month") {
    const monthlyFloor = capacityMw * floorAmount;
    rawFloor = monthlyFloor;
    rawFloorPerSettlement = monthlyFloor * settlementMonths;
    rawFloorUnit = "month";
  } else if (normalized.floorInputMode === "eur_per_kw_month") {
    const monthlyFloor = capacityMw * 1000 * floorAmount;
    rawFloor = monthlyFloor;
    rawFloorPerSettlement = monthlyFloor * settlementMonths;
    rawFloorUnit = "month";
  }

  const effectiveFloorPerSettlement = rawFloorPerSettlement * (availabilityPct / 100);

  return {
    rawFloor,
    rawFloorUnit,
    rawFloorPerSettlement,
    availabilityPct,
    effectiveFloorPerSettlement,
    annualizedEffectiveFloor: effectiveFloorPerSettlement * periodsPerYear,
    settlementFrequency: normalized.settlementFrequency,
  };
}

export function prepareTollingFloorUpsideForPersistence(config = {}) {
  const normalized = normalizeTollingFloorUpside(config);
  const bankability = normalized.bankability || DEFAULT_TOLLING_FLOOR_UPSIDE.bankability;
  const performanceRegime =
    normalized.performanceRegime || DEFAULT_TOLLING_FLOOR_UPSIDE.performanceRegime;

  return {
    ...normalized,
    floorAmount: Math.max(0, parseFiniteNumber(normalized.floorAmount, 0)),
    applyAvailabilityToFloor: Boolean(normalized.applyAvailabilityToFloor),
    availabilityAdjustmentPct: clampPercent(normalized.availabilityAdjustmentPct, 100),
    effectiveFloorPreview: null,
    contractedCapacityMw: parseOptionalFiniteNumber(normalized.contractedCapacityMw),
    projectUpsideSharePct: clampPercent(normalized.projectUpsideSharePct, 0),
    contractTermYears: Math.max(0, parseFiniteNumber(normalized.contractTermYears, 0)),
    merchantOptimization: Boolean(normalized.merchantOptimization),
    bankability: {
      ...DEFAULT_TOLLING_FLOOR_UPSIDE.bankability,
      ...bankability,
      counterpartyName: String(bankability.counterpartyName ?? ""),
      creditSupportMonths:
        bankability.creditSupportType === "none"
          ? null
          : parseOptionalFiniteNumber(bankability.creditSupportMonths),
    },
    performanceRegime: {
      ...DEFAULT_TOLLING_FLOOR_UPSIDE.performanceRegime,
      ...performanceRegime,
      guaranteedAvailabilityPct:
        performanceRegime.guaranteedAvailabilityPct == null
          ? null
          : clampPercent(performanceRegime.guaranteedAvailabilityPct, 0),
      penaltyCapPctOfAnnualFloor:
        performanceRegime.penaltyCapPctOfAnnualFloor == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.penaltyCapPctOfAnnualFloor, 0)),
      plannedOutageAllowanceHours:
        performanceRegime.plannedOutageAllowanceHours == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.plannedOutageAllowanceHours, 0)),
      guaranteedRtePct:
        performanceRegime.guaranteedRtePct == null
          ? null
          : clampPercent(performanceRegime.guaranteedRtePct, 0),
      maxAnnualCycles:
        performanceRegime.maxAnnualCycles == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.maxAnnualCycles, 0)),
      maxAnnualThroughputMWh:
        performanceRegime.maxAnnualThroughputMWh == null
          ? null
          : Math.max(0, parseFiniteNumber(performanceRegime.maxAnnualThroughputMWh, 0)),
    },
  };
}

export function normalizeProjectTollingFloorUpside(config = {}) {
  const tollingFloorUpside = normalizeTollingFloorUpside(config);

  return {
    ...config,
    tollingFloorUpside,
    tollingRevenueFloor: tollingFloorUpside.floorAmount,
    tollingUpsideSharePct: tollingFloorUpside.projectUpsideSharePct,
    tollingContractYears: tollingFloorUpside.contractTermYears,
    tollingAvailabilityPct: tollingFloorUpside.availabilityAdjustmentPct,
    tollingSettlementFrequency: tollingFloorUpside.settlementFrequency,
    tollingEscalationPct: tollingFloorUpside.floorEscalationPct,
    tollingCapacityBasis: tollingFloorUpside.capacityBasis,
    useMerchantOptimization: Boolean(tollingFloorUpside.merchantOptimization),
  };
}

export function prepareProjectTollingFloorUpsideForPersistence(config = {}) {
  const tollingFloorUpside = prepareTollingFloorUpsideForPersistence(config);

  return {
    ...config,
    tollingFloorUpside,
    tollingRevenueFloor: tollingFloorUpside.floorAmount,
    tollingUpsideSharePct: tollingFloorUpside.projectUpsideSharePct,
    tollingContractYears: tollingFloorUpside.contractTermYears,
    tollingAvailabilityPct: tollingFloorUpside.availabilityAdjustmentPct,
    tollingSettlementFrequency: tollingFloorUpside.settlementFrequency,
    tollingEscalationPct: tollingFloorUpside.floorEscalationPct,
    tollingCapacityBasis: tollingFloorUpside.capacityBasis,
    useMerchantOptimization: Boolean(tollingFloorUpside.merchantOptimization),
  };
}
